import 'reflect-metadata'
import { applyDecorators, Get, Post, Put, Patch, Delete, SetMetadata, HttpCode } from '@nestjs/common'
import { ApiOperation, ApiParam, ApiOkResponse, ApiBody } from '../utils/safeSwagger'
import { TypeormAdapter } from '../adapters/typeorm.adapter'
import { CrudmanRegistry } from '../module/CrudmanRegistry'

const CRD_META = 'crudman:meta'

export const UseCrud = (config: any, options?: any): ClassDecorator => {
  return (target: any) => {
    const opts = { defaultOrm: TypeormAdapter, ...(options || {}) }
    const existing = (global as any).__crudman_global_meta || { config: { sections: {} }, options: {} }
    // Shorthand: models → sections
    const shorthandToSections = (): Record<string, any> => {
      const out: Record<string, any> = {}
      const models = config?.models
      const shared = config?.defaults || {}
      const toPlural = (s: string) => {
        const lower = String(s || '').toLowerCase()
        if (/(s|x|z|ch|sh)$/.test(lower)) return lower + 'es'
        if (/y$/.test(lower) && !/[aeiou]y$/.test(lower)) return lower.replace(/y$/, 'ies')
        return lower + 's'
      }
      const apply = (model: any, overrides?: any) => {
        const name = model?.name || model?.model?.name || 'resource'
        const section = overrides?.section || toPlural(name)
        out[section] = { model, ...shared, ...(overrides || {}) }
      }
      if (!models) return out
      if (Array.isArray(models)) {
        for (const m of models) {
          if (Array.isArray(m)) apply(m[0], m[1] || {})
          else apply(m)
        }
      } else if (typeof models === 'object') {
        for (const key of Object.keys(models)) apply({ name: key }, models[key])
      }
      return out
    }
    // Deep merge sections to avoid overwriting existing detailed configurations with empty objects
    const deepMergeSections = (existing: any, incoming: any) => {
      const result = { ...existing }
      for (const [key, value] of Object.entries(incoming || {})) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // If the incoming value is an object (not empty), merge it
          if (Object.keys(value).length > 0) {
            result[key] = { ...(result[key] || {}), ...value }
          }
          // If it's an empty object, don't overwrite existing configuration
        } else {
          result[key] = value
        }
      }
      return result
    }
    
    const mergedSections = deepMergeSections(existing.config?.sections || {}, { ...(config?.sections || {}), ...shorthandToSections() })
    const mergedConfig = { ...existing.config, ...config, sections: mergedSections }
    const mergedOptions = { ...existing.options, ...opts }
    const meta = { config: mergedConfig, options: mergedOptions }
    Reflect.defineMetadata(CRD_META, meta, target)
    ;(global as any).__crudman_global_meta = meta
  }
}

export const CrudList = (section: string, opts?: any) => applyDecorators(
  SetMetadata('crudman:action', { section, action: 'list', opts }),
  ApiOperation({ summary: `${section}: list` }),
  ApiOkResponse({ description: 'List response' }),
  Get()
)
export const CrudDetails = (section: string, opts?: any) => applyDecorators(
  SetMetadata('crudman:action', { section, action: 'details', opts }),
  ApiOperation({ summary: `${section}: details` }),
  ApiParam({ name: 'id', required: true }),
  ApiOkResponse({ description: 'Details response' }),
  Get(':id')
)
export const CrudCreate = (section: string, opts?: any) => applyDecorators(
  SetMetadata('crudman:action', { section, action: 'create', opts }),
  ApiOperation({ summary: `${section}: create` }),
  ApiOkResponse({ description: 'Create response' }),
  Post(),
  HttpCode(200)
)
export const CrudUpdate = (section: string, opts?: any) => {
  const method = CrudmanRegistry.get().getUpdateMethod()
  const verbDecorator = method === 'patch' ? Patch(':id') : Put(':id')
  return applyDecorators(
    SetMetadata('crudman:action', { section, action: 'update', opts }),
    ApiOperation({ summary: `${section}: update` }),
    ApiParam({ name: 'id', required: true }),
    ApiOkResponse({ description: 'Update response' }),
    verbDecorator
  )
}
export const CrudDelete = (section: string, opts?: any) => applyDecorators(
  SetMetadata('crudman:action', { section, action: 'delete', opts }),
  ApiOperation({ summary: `${section}: delete` }),
  ApiParam({ name: 'id', required: true }),
  ApiOkResponse({ description: 'Delete response' }),
  Delete(':id')
)
export const CrudSave = (section: string, opts?: any) => applyDecorators(
  SetMetadata('crudman:action', { section, action: 'save', opts }),
  ApiOperation({ summary: `${section}: save (upsert)` }),
  ApiOkResponse({ description: 'Save response' }),
  Post(),
  HttpCode(200)
)

export const getCrudMeta = (target: any) => Reflect.getMetadata(CRD_META, target.constructor || target)

// Bulk operations
export const CrudBulkImport = (section: string, opts?: any) => applyDecorators(
  SetMetadata('crudman:action', { section, action: 'bulkImport', opts }),
  ApiOperation({ summary: `${section}: bulk import` }),
  ApiBody({ description: 'Array of JSON records or CSV text when x-content-type=csv', required: true }),
  ApiOkResponse({ description: 'Bulk import summary' }),
  Post('bulk/import'),
  HttpCode(200)
)

export const CrudBulkDelete = (section: string, opts?: any) => applyDecorators(
  SetMetadata('crudman:action', { section, action: 'bulkDelete', opts }),
  ApiOperation({ summary: `${section}: bulk delete` }),
  ApiBody({ description: 'Body: { ids: [] } or CSV when x-content-type=csv', required: true }),
  ApiOkResponse({ description: 'Bulk delete summary' }),
  Post('bulk/delete'),
  HttpCode(200)
)


